import type { Campaign, CampaignCreative } from '../campaignsApi';
import { campaignChannels, channelLabel } from '../campaignChannels';
import { campaignIsApproved, campaignPublicationChecks } from '../campaignJourney';
import { CampaignStatusButton } from './CampaignStatusButton';
import './campaign-journey.css';

interface Props {
  campaign: Campaign;
  creatives: CampaignCreative[];
  status: string;
  approving: boolean;
  metaPending: boolean;
  googlePending: boolean;
  onApprove: () => void;
  onPublishMeta: () => void;
  onPublishGoogle: () => void;
  onConfigure: () => void;
  onReview: () => void;
}

export function CampaignPublicationJourney(props: Props) {
  const channels = campaignChannels(props.campaign);
  const approved = campaignIsApproved(props.status);
  const hasPublished = Boolean(props.campaign.meta_campaign_id || props.campaign.google_campaign_id);
  const prepared = channels.some(channel => campaignPublicationChecks(channel, props.campaign, props.creatives).every(check => check.ready));
  return <section className="campaign-publication-journey">
    <h3>3. Aprovar a revisão e publicar em cada canal</h3>
    <p>A aprovação prepara a campanha. A publicação acontece somente no botão do canal escolhido.</p>
    <div className="campaign-journey-approval">
      <strong>{approved ? 'Revisão aprovada para publicação' : 'A revisão dos anúncios ainda precisa de aprovação'}</strong>
      {!approved && <button type="button" className="campaign-journey-button" disabled={props.approving || !prepared} onClick={props.onApprove}>
        {props.approving ? 'Aprovando...' : 'Aprovar revisão dos anúncios'}
      </button>}
    </div>
    {!approved && !prepared && <p>Prepare pelo menos um canal antes de aprovar a revisão.</p>}
    {channels.length > 1 && <p>Canais com pendências continuam bloqueados. Você pode publicar primeiro o canal que estiver pronto.</p>}
    <div className="campaign-publication-cards">
      {channels.map(channel => {
        const meta = channel === 'meta';
        const id = meta ? props.campaign.meta_campaign_id : props.campaign.google_campaign_id;
        const account = meta ? props.campaign.meta_ad_account_id : props.campaign.google_customer_id;
        const error = meta ? props.campaign.meta_last_error : props.campaign.google_last_error;
        const pending = meta ? props.metaPending : props.googlePending;
        const checks = campaignPublicationChecks(channel, props.campaign, props.creatives);
        const ready = checks.every(check => check.ready);
        return <article key={channel} className="campaign-publication-card">
          <h4>{channelLabel(channel)}</h4>
          <p>{meta ? 'Facebook e Instagram · Anúncios com artes ou vídeos.' : 'Pesquisa Google · Anúncios de texto e palavras-chave.'}</p>
          <dl><dt>Conta de publicação</dt><dd>{account || 'Ainda não selecionada'}</dd>
            <dt>Campanha na origem</dt><dd>{id || 'Ainda não publicada'}</dd></dl>
          <ul className="campaign-publication-checks">{checks.map(check => <li key={check.label} data-ready={check.ready}>{check.ready ? '✓' : '○'} {check.label}</li>)}</ul>
          {error && <p role="alert" className="campaign-channel-error">{error}</p>}
          {!ready && <div className="campaign-journey-links">
            <button type="button" onClick={props.onConfigure}>Voltar ao briefing</button>
            <button type="button" onClick={props.onReview}>Revisar anúncios</button>
          </div>}
          <button type="button" className="campaign-journey-button" disabled={!approved || !ready || pending || props.approving}
            onClick={meta ? props.onPublishMeta : props.onPublishGoogle}>
            {pending ? 'Sincronizando...' : `${id ? 'Atualizar anúncios no' : 'Publicar no'} ${channelLabel(channel)}`}
          </button>
          {!approved && <small>Aprove a revisão acima para liberar a publicação.</small>}
          {!id && <small>A nova campanha será criada pausada. Confira o resultado antes de ativar.</small>}
          {id && !meta && <small>Envia os títulos, descrições e destino revisados. Insere ou reativa palavras-chave selecionadas e pausa as positivas ausentes da lista neste grupo. Mantém palavras negativas, status e orçamento.</small>}
        </article>;
      })}
    </div>
    {hasPublished && <div className="campaign-journey-activation">
      <strong>Veiculação dos canais vinculados</strong>
      <p>Publicar e começar a veicular são passos separados. A alteração de status abaixo afeta as origens já vinculadas.</p>
      <CampaignStatusButton campaign={{ ...props.campaign, status: props.status,
        ad_platform: props.campaign.meta_campaign_id ? props.campaign.google_campaign_id ? 'both' : 'meta' : 'google' }} />
    </div>}
  </section>;
}
